import React, { useCallback, useRef, useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { PromoCode, GiftCertificateFormData, GiftCode, TFormReset } from '../Summary.definitions';
import { AccordionSection } from '../components/AccordionSection';
import { useBasket } from '~/features/basket';
import { useTranslation } from '~/shared/utils/translation';
import useProducts from '~/features/commerce-api/hooks/useProducts';
import { useFrame } from '~/shared/utils';
import { useEvents } from '~/shared/hooks/useEvents';
import { GiftCertificateForm } from './components/GiftCertificateForm';
import { StyledActiveCodes } from './components/ActiveCodes';
import { PromoCodeForm } from './components/PromoCodeForm';
import { GiftCertificateCaption } from './components/styled';
import Flex from '../../Flex';
import { GiftCardError } from '~/features/commerce-api/http-error';
import { TranslationKey } from '~/lib';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import {
    _matchCouponsFromCartWithProducts,
    _getCouponsFromOrder,
} from '~/shared/hooks/useEvents/useGTMEvents/helper';
import { _transformProductToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { VoucherList } from './components/VoucherList';
import { useActiveVouchers } from '~/shared/hooks/loyalty';
import styles from '../Summary.module.scss';
import { StyledInvalidMessage } from '../../form/components/invalid-message';
import clsx from 'clsx';

enum CouponEventType {
    Add = 'ADD',
    Remove = 'REMOVE',
}

export type PromoCodeSectionProps = {
    discountCodeTitle: string;
    giftCardTitle: string;
    titleAddon?: string | number;
    submitLabel: string;
};

export const PromoCodeSection = styled(
    ({ discountCodeTitle, giftCardTitle, titleAddon, submitLabel }: PromoCodeSectionProps) => {
        const basket = useBasket();
        const [error, setError] = useState<string | undefined>(undefined);
        const [giftCardError, setGiftCardError] = useState<string | undefined>(undefined);
        const { translate } = useTranslation();
        const formRef = useRef<TFormReset>();
        const giftCertificateRef = useRef<TFormReset>();
        const { products } = useProducts();
        const { data: frame } = useFrame();
        const { addCouponCodeEvent, removeCouponCodeEvent } = useEvents(frame);
        const { isStorePersonnel, isPlusMember } = useCustomer();
        const couponEventRef = useRef<CouponEventType | boolean>(false);
        const couponCodeRef = useRef<string>('');
        const { data: vouchers } = useActiveVouchers({
            select: (data) =>
                data.value.map(({ couponNo, expirationDate, barcode, description }) => ({
                    couponNo,
                    expirationDate,
                    barcode,
                    description,
                })),
        });

        /**
         * Effect for handling coupon code events.
         * Triggers the appropriate coupon code event based on the coupon event type.
         * Runs when there is a coupon event.
         */
        useEffect(() => {
            if (!couponEventRef.current) return;

            const isAddEvent = couponEventRef.current === CouponEventType.Add;
            const productCoupons = _matchCouponsFromCartWithProducts(products, basket.couponItems);
            const orderCoupons = _getCouponsFromOrder(basket);

            const couponCodeEvent = isAddEvent ? addCouponCodeEvent : removeCouponCodeEvent;
            couponCodeEvent(
                (products ?? []).map((item, index) => {
                    item.coupon = productCoupons?.appliedCoupons[item.id]?.couponCode;
                    return _transformProductToDataLayer(item, item.quantity, index);
                }),
                String(basket.orderTotal),
                orderCoupons,
            );

            couponEventRef.current = false;
            couponCodeRef.current = '';
        }, [basket, couponEventRef]);

        const nonVoucherPromoCodes =
            basket.couponItems
                ?.filter((x) => !vouchers?.some((v) => v.barcode === x.code))
                .map((x) => ({
                    code: x.code,
                    name: translate('checkout.promoSection.promoName'),
                    id: x.couponItemId ?? '',
                    valid: x.valid,
                })) ?? [];

        useEffect(() => {
            if (!basket.couponItems?.length) return;

            const expiredCoupon = basket.couponItems.find((item) => {
                if (!item.c_endDate) return false;

                return new Date() > new Date(item.c_endDate);
            });

            if (expiredCoupon)
                handleRemove({
                    name: expiredCoupon.name,
                    code: expiredCoupon.couponItemId ?? '',
                });
        }, [basket.couponItems]);

        const activeGiftCertificates =
            basket.paymentInstruments
                ?.filter((x) => x.maskedGiftCertificateCode)
                .map((x) => ({
                    name: translate('checkout.giftCertificateForm.cardName'),
                    code: x.maskedGiftCertificateCode ?? '',
                    id: x.paymentInstrumentId ?? '',
                })) ?? [];

        const handlePromoCode = useCallback(
            async (promoCode: Record<string, string>) => {
                try {
                    couponEventRef.current = CouponEventType.Add;
                    setError(undefined);

                    await basket.applyPromoCode(promoCode.code ?? '');

                    formRef?.current?.reset();
                } catch (e) {
                    if (e instanceof Error) {
                        return setError(translate('basket.invalidCoupon') ?? e.message);
                    }
                    throw e;
                }
            },
            [basket, formRef],
        );

        const handleRemove = useCallback(
            async (promoCode: PromoCode) => {
                try {
                    const currentPromoCode = nonVoucherPromoCodes.find(
                        (item) => item.id === promoCode.code,
                    )?.code;
                    couponEventRef.current = CouponEventType.Remove;
                    couponCodeRef.current = currentPromoCode || '';
                    setError(undefined);

                    await basket.removePromoCode(promoCode.code);
                } catch (e) {
                    if (e instanceof Error) {
                        return setError(e.message);
                    }
                    throw e;
                }
            },
            [basket],
        );

        const addGiftCertificate = useCallback(
            async ({ card, cvc }: GiftCertificateFormData) => {
                try {
                    await basket.addGiftCertificatePayment(card, cvc);
                    giftCertificateRef.current?.reset();
                    setGiftCardError(undefined);
                } catch (e) {
                    if (e instanceof Error) {
                        switch (true) {
                            case e instanceof GiftCardError:
                                setGiftCardError(
                                    translate(
                                        ('checkout.giftCertificateForm.error.' +
                                            (e as GiftCardError).message) as TranslationKey,
                                    ),
                                );
                                break;
                            default:
                                setGiftCardError(
                                    translate(
                                        'checkout.giftCertificateForm.error.GC_GENERAL_ERROR',
                                    ),
                                );
                        }
                    } else {
                        throw e;
                    }
                }
            },
            [basket],
        );

        const removeGiftCertificate = useCallback(
            async (code: GiftCode) => {
                try {
                    await basket.removeGiftCertificatePayment(code.id);
                    setGiftCardError(undefined);
                } catch (e) {
                    if (e instanceof Error) {
                        e.message = translate('checkout.giftCertificateForm.error.removeError');
                        setGiftCardError(e.message);
                    } else {
                        throw e;
                    }
                }
            },
            [basket],
        );

        return (
            <div className={styles.promotionCodeWrapper}>
                <div
                    className={clsx(styles.promotionCodeSectionItem, {
                        [styles.isInStore]: isStorePersonnel,
                    })}
                >
                    <AccordionSection
                        initOpenForSEO
                        id="promo-discount"
                        title={discountCodeTitle}
                        titleAddon={titleAddon}
                        onToggle={() => setError(undefined)}
                    >
                        <>
                            <PromoCodeForm
                                submitLabel={submitLabel}
                                name="code"
                                onSubmit={handlePromoCode}
                                ref={formRef}
                            />
                            {!!error && <StyledInvalidMessage>{error}</StyledInvalidMessage>}
                            <StyledActiveCodes
                                activeCodes={nonVoucherPromoCodes}
                                onRemove={(x) =>
                                    handleRemove({
                                        code: x.id,
                                        name: x.name,
                                    })
                                }
                            />
                            {isPlusMember && vouchers && vouchers?.length > 0 && (
                                <VoucherList vouchers={vouchers} />
                            )}
                        </>
                    </AccordionSection>
                </div>
                {!isStorePersonnel && (
                    <AccordionSection initOpenForSEO id="promo-giftcard" title={giftCardTitle}>
                        <>
                            <Flex spacingBottom="small">
                                <GiftCertificateCaption variant="caption">
                                    {translate('checkout.giftCertificateForm.caption')}
                                </GiftCertificateCaption>
                            </Flex>
                            <GiftCertificateForm
                                name="giftCertificate"
                                submitLabel={submitLabel}
                                onSubmit={addGiftCertificate}
                                giftCardError={giftCardError}
                                ref={giftCertificateRef}
                                onChange={() => giftCardError && setGiftCardError(undefined)}
                            />
                        </>

                        <StyledActiveCodes
                            activeCodes={activeGiftCertificates}
                            onRemove={(x) => removeGiftCertificate({ name: x.code, id: x.id })}
                        />
                    </AccordionSection>
                )}
            </div>
        );
    },
)({});
